export interface LoomPresetSelectedComponent {
  componentId: string;
  role: "wing" | "fuselage";
}

export interface LoomPresetOmittedComponent {
  componentId: string;
  name: string;
  reason: string;
  type: string;
}

export interface LoomPresetAdaptation {
  componentId: string;
  derivedValue: boolean | number | string | null;
  kind: string;
  reason: string;
  sourceValue: boolean | number | string | null;
}

export interface LoomPresetNormalizationReceipt {
  metresPerNativeUnit: number;
  policyId: "loom.airshow-body-length-normalization/v1";
  sourceBodyLengthNative: number;
  sourceLengthUnit: "unbound";
  targetBodyLengthM: 10;
}

export interface LoomPresetReceipt {
  schema: "loom.preset-receipt/v1";
  adaptations: readonly LoomPresetAdaptation[];
  archiveMemberPath: string;
  catalogId: string;
  catalogRevision: string;
  definitionRevision: string;
  entryId: string;
  entryLabel: string;
  normalization: LoomPresetNormalizationReceipt;
  omittedComponents: readonly LoomPresetOmittedComponent[];
  presetRevision: string;
  selectedComponents: readonly LoomPresetSelectedComponent[];
  sourceAssetSha256: string;
  sourceScope: "selected-components-only";
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  const missing = allowed.filter((key) => !(key in value));
  if (unknown.length || missing.length) throw new Error(`${path} has an unsupported field set`);
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${path} must be a non-empty string`);
  }
  return value;
}

function scalar(value: unknown, path: string): boolean | number | string | null {
  if (value === null || typeof value === "boolean" || typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  throw new Error(`${path} must be a finite scalar`);
}

function positive(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`${path} must be positive`);
  }
  return value;
}

function sha256(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^(?:sha256:)?[a-f0-9]{64}$/.test(result)) {
    throw new Error(`${path} must be a SHA-256 identity`);
  }
  return result;
}

function entryId(value: unknown, path: string): string {
  const result = text(value, path);
  if (!/^airshow\/[a-z0-9._-]+\/[a-z0-9._-]+$/.test(result)) {
    throw new Error(`${path} is invalid`);
  }
  return result;
}

function memberPath(value: unknown, path: string): string {
  const result = text(value, path);
  if (result.startsWith("/") || result.includes("\\") || result.split("/").includes("..")) {
    throw new Error(`${path} must be a safe archive member path`);
  }
  return result;
}

function parseSelected(value: unknown): LoomPresetSelectedComponent[] {
  if (!Array.isArray(value) || value.length !== 2) {
    throw new Error("command.receipt.selectedComponents must contain one wing and one fuselage");
  }
  const parsed = value.map((item, index): LoomPresetSelectedComponent => {
    const path = `command.receipt.selectedComponents[${index}]`;
    const row = record(item, path);
    exactKeys(row, ["componentId", "role"], path);
    if (row.role !== "wing" && row.role !== "fuselage") {
      throw new Error("command.receipt.selectedComponents role is unsupported");
    }
    return { componentId: text(row.componentId, `${path}.componentId`), role: row.role };
  });
  if (new Set(parsed.map(({ componentId }) => componentId)).size !== parsed.length
      || new Set(parsed.map(({ role }) => role)).size !== 2) {
    throw new Error("command.receipt.selectedComponents must identify distinct wing and fuselage sources");
  }
  return parsed;
}

function parseOmissions(value: unknown): LoomPresetOmittedComponent[] {
  if (!Array.isArray(value)) throw new Error("command.receipt.omittedComponents must be an array");
  const parsed = value.map((item, index): LoomPresetOmittedComponent => {
    const path = `command.receipt.omittedComponents[${index}]`;
    const row = record(item, path);
    exactKeys(row, ["componentId", "name", "type", "reason"], path);
    return {
      componentId: text(row.componentId, `${path}.componentId`),
      name: text(row.name, `${path}.name`),
      reason: text(row.reason, `${path}.reason`),
      type: text(row.type, `${path}.type`),
    };
  });
  if (new Set(parsed.map(({ componentId }) => componentId)).size !== parsed.length) {
    throw new Error("command.receipt.omittedComponents contains duplicates");
  }
  return parsed;
}

function parseAdaptations(value: unknown): LoomPresetAdaptation[] {
  if (!Array.isArray(value)) throw new Error("command.receipt.adaptations must be an array");
  return value.map((item, index): LoomPresetAdaptation => {
    const path = `command.receipt.adaptations[${index}]`;
    const row = record(item, path);
    exactKeys(row, ["kind", "componentId", "reason", "sourceValue", "derivedValue"], path);
    return {
      componentId: text(row.componentId, `${path}.componentId`),
      derivedValue: scalar(row.derivedValue, `${path}.derivedValue`),
      kind: text(row.kind, `${path}.kind`),
      reason: text(row.reason, `${path}.reason`),
      sourceValue: scalar(row.sourceValue, `${path}.sourceValue`),
    };
  });
}

function parseNormalization(value: unknown): LoomPresetNormalizationReceipt {
  const row = record(value, "command.receipt.normalization");
  exactKeys(row, [
    "policyId", "sourceLengthUnit", "sourceBodyLengthNative",
    "targetBodyLengthM", "metresPerNativeUnit",
  ], "command.receipt.normalization");
  if (row.policyId !== "loom.airshow-body-length-normalization/v1"
      || row.sourceLengthUnit !== "unbound" || row.targetBodyLengthM !== 10) {
    throw new Error("command.receipt.normalization policy is unsupported");
  }
  return {
    metresPerNativeUnit: positive(
      row.metresPerNativeUnit, "command.receipt.normalization.metresPerNativeUnit",
    ),
    policyId: row.policyId,
    sourceBodyLengthNative: positive(
      row.sourceBodyLengthNative, "command.receipt.normalization.sourceBodyLengthNative",
    ),
    sourceLengthUnit: row.sourceLengthUnit,
    targetBodyLengthM: row.targetBodyLengthM,
  };
}

export function parseLoomPresetReceipt(value: unknown): LoomPresetReceipt {
  const row = record(value, "command.receipt");
  exactKeys(row, [
    "schema", "catalogId", "catalogRevision", "entryId", "entryLabel", "presetRevision",
    "definitionRevision", "archiveMemberPath", "sourceAssetSha256", "sourceScope",
    "normalization", "selectedComponents", "omittedComponents", "adaptations",
  ], "command.receipt");
  if (row.schema !== "loom.preset-receipt/v1" || row.sourceScope !== "selected-components-only") {
    throw new Error("command.receipt schema or source scope is unsupported");
  }
  return {
    schema: row.schema,
    adaptations: parseAdaptations(row.adaptations),
    archiveMemberPath: memberPath(row.archiveMemberPath, "command.receipt.archiveMemberPath"),
    catalogId: text(row.catalogId, "command.receipt.catalogId"),
    catalogRevision: sha256(row.catalogRevision, "command.receipt.catalogRevision"),
    definitionRevision: sha256(row.definitionRevision, "command.receipt.definitionRevision"),
    entryId: entryId(row.entryId, "command.receipt.entryId"),
    entryLabel: text(row.entryLabel, "command.receipt.entryLabel"),
    normalization: parseNormalization(row.normalization),
    omittedComponents: parseOmissions(row.omittedComponents),
    presetRevision: sha256(row.presetRevision, "command.receipt.presetRevision"),
    selectedComponents: parseSelected(row.selectedComponents),
    sourceAssetSha256: sha256(row.sourceAssetSha256, "command.receipt.sourceAssetSha256"),
    sourceScope: row.sourceScope,
  };
}
